import { useParams, Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import NotFound from "@/pages/NotFound";

const CONTENT = {
  shipping: {
    overline: "POLICIES",
    title: "Shipping Policy",
    sections: [
      { h: "PROCESSING", p: "Orders are processed within 1–2 business days. You'll receive a confirmation email the moment your order is placed, and tracking as soon as it ships." },
      { h: "STANDARD SHIPPING — $5.00", p: "Delivered in 5–7 business days. Available on every order, worldwide." },
      { h: "EXPRESS SHIPPING — $12.00", p: "Delivered in 2–3 business days for orders that can't wait." },
      { h: "WORLDWIDE DELIVERY", p: "We ship internationally. Duties and import taxes for international orders are calculated and shown at checkout where applicable." },
      { h: "TRACKING", p: "Tracking information is emailed automatically when your order leaves the studio. You can also check status anytime on the Track Order page." },
    ],
  },
  returns: {
    overline: "POLICIES",
    title: "Returns & Exchanges",
    sections: [
      { h: "30-DAY WINDOW", p: "Returns are accepted within 30 days of delivery. Items must be unworn, unwashed, and with original tags attached." },
      { h: "HOW TO START A RETURN", p: "Contact us through the Contact page with your order number (DR-XXXXXX) and we'll send return instructions within 1–2 business days." },
      { h: "EXCHANGES", p: "Need a different size? Exchanges are free within the 30-day window, subject to stock. Limited drops may sell out — in that case we'll issue a full refund." },
      { h: "REFUNDS", p: "Refunds are issued to the original payment method within 5–10 business days of the returned item arriving back at our studio." },
    ],
  },
  privacy: {
    overline: "POLICIES",
    title: "Privacy Policy",
    sections: [
      { h: "WHAT WE COLLECT", p: "Only what's needed to run the store: your contact details, shipping address, and order history. Payment details are processed entirely by our encrypted payment provider — we never see or store your card number." },
      { h: "HOW WE USE IT", p: "To fulfill your order, send order updates, and — only if you join the list — early access to drops. We never sell your data." },
      { h: "COOKIES", p: "We use minimal, functional cookies to keep your cart working. No invasive tracking." },
      { h: "YOUR RIGHTS", p: "You can request a copy or deletion of your data anytime by contacting support@dejonrichh.com." },
    ],
  },
  terms: {
    overline: "POLICIES",
    title: "Terms & Conditions",
    sections: [
      { h: "THE BRAND", p: "Dejon Richh sells original streetwear designed as complete outfits. All artwork — including the DR mark and cross designs — is original and protected." },
      { h: "ORDERS", p: "Prices are listed in USD. We reserve the right to cancel orders placed in error; you'll always be refunded in full if that happens." },
      { h: "LIMITED DROPS", p: "Many pieces are produced in limited runs. Availability is not guaranteed and sold-out items may not restock." },
      { h: "LIABILITY", p: "Our liability is limited to the purchase price of the item. Nothing here limits your statutory consumer rights." },
    ],
  },
  faq: {
    overline: "HELP",
    title: "FAQ",
    sections: [
      { h: "HOW DOES SIZING RUN?", p: "The Classic Tee is a relaxed streetwear fit and runs true to size. Size down for a closer fit, up for an oversized look. Sizes run S through 2XL and are unisex." },
      { h: "DO I NEED AN ACCOUNT TO ORDER?", p: "No. Checkout is guest-only by design — email, shipping and payment, done." },
      { h: "WHEN IS THE NEXT DROP?", p: "DR1 Graffiti, Hero and Crochet collections are in the works. Join the newsletter list at the bottom of any page for first access." },
      { h: "HOW DO I TRACK MY ORDER?", p: "Use the Track Order page with your order number and checkout email. Tracking links are also emailed when your order ships." },
      { h: "CAN I CHANGE MY ORDER?", p: "Contact us as soon as possible — if it hasn't shipped, we'll do our best to update size or address." },
    ],
  },
};

export default function Policy() {
  const { topic } = useParams();
  const page = CONTENT[topic];
  if (!page) return <NotFound />;

  return (
    <div data-testid={`policy-page-${topic}`}>
      <PageHeader overline={page.overline} title={page.title} />
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8 py-14">
        <div className="border-t border-wire">
          {page.sections.map((s, i) => (
            <div key={s.h} className="grid grid-cols-1 gap-3 border-b border-wire py-10 sm:grid-cols-12 sm:gap-8" data-testid={`policy-section-${i}`}>
              <p className="font-mono text-[11px] tracking-[0.3em] text-neutral-300 sm:col-span-4">{s.h}</p>
              <p className="font-mono text-xs leading-relaxed tracking-wider text-neutral-400 sm:col-span-8">{s.p}</p>
            </div>
          ))}
        </div>
        <p className="mt-10 font-mono text-[10px] tracking-[0.25em] text-neutral-600">
          STILL HAVE QUESTIONS? <Link to="/contact" data-testid="policy-contact-link" className="text-neutral-300 underline underline-offset-4 hover:text-white">CONTACT US</Link>
        </p>
      </div>
    </div>
  );
}
